/**
 * MessageGraph — in-memory mirror of chat messages.
 * - NO time-based filtering. Every message ever mirrored is kept until
 *   clearChat() or clear() is called explicitly.
 * - graph = { [messageId]: MessageNode } — one entry per unique message id.
 * - Write via mirror() is idempotent: the latest payload wins (upsert).
 * - getOrdered() returns ALL messages for a chat sorted by createdAt ascending.
 */

export interface GraphSnapshot {
  count: number;
  oldest: string | null;
  newest: string | null;
}

class MessageGraph {
  private graph = new Map<string, any>();
  private chatIndex = new Map<string, Set<string>>();

  /** Upsert a message. Latest write wins. No filtering applied. */
  mirror(chatId: string, msg: any): void {
    if (!msg?.id) return;
    const id = String(msg.id);
    this.graph.set(id, msg);
    if (!this.chatIndex.has(chatId)) {
      this.chatIndex.set(chatId, new Set());
    }
    this.chatIndex.get(chatId)!.add(id);
  }

  has(id: string): boolean {
    return this.graph.has(String(id));
  }

  get(id: string): any {
    return this.graph.get(String(id));
  }

  /** All messages for chatId, sorted oldest → newest. No filtering. */
  getOrdered(chatId: string): any[] {
    const ids = this.chatIndex.get(chatId);
    if (!ids) return [];
    return Array.from(ids)
      .map((id) => this.graph.get(id))
      .filter(Boolean)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
  }

  /** Total messages held for a chat. */
  size(chatId: string): number {
    return this.chatIndex.get(chatId)?.size ?? 0;
  }

  /** Diagnostic snapshot — count, oldest and newest createdAt strings. */
  snapshot(chatId: string): GraphSnapshot {
    const ordered = this.getOrdered(chatId);
    return {
      count: ordered.length,
      oldest: ordered[0]?.createdAt ?? null,
      newest: ordered[ordered.length - 1]?.createdAt ?? null,
    };
  }

  clearChat(chatId: string): void {
    const ids = this.chatIndex.get(chatId);
    if (ids) {
      ids.forEach((id) => this.graph.delete(id));
      this.chatIndex.delete(chatId);
    }
  }

  /** Remove a single message from the graph and all chat indexes. */
  remove(messageId: string): void {
    const id = String(messageId);
    this.graph.delete(id);
    this.chatIndex.forEach((ids) => {
      ids.delete(id);
    });
  }

  /** Bulk-insert messages from IDB cache — used for crash recovery on chat switch. */
  rebuildFromMessages(chatId: string, messages: any[]): void {
    messages.forEach((msg) => this.mirror(chatId, msg));
  }

  clear(): void {
    this.graph.clear();
    this.chatIndex.clear();
  }
}

export const messageGraph = new MessageGraph();
